// src/api/auth.ts
import { msalConfig, apiLoginRequest } from "../config/authConfig";
import { msalInstance } from "../utils/msalInit";
import { logger } from "../utils/logger";

// Important: Initialize the MSAL instance before using it
// This ensures the library is properly initialized before any login attempts
const msalInitPromise = msalInstance.initialize();

// Helper to log detailed error information
const logError = (method: string, error: any) => {
  // Always log basic error info
  logger.error(`Auth Error in ${method}`);
  
  // In development, log additional details
  if (process.env.NODE_ENV !== 'production') {
    if (error.errorCode) {
      logger.error(`Error Code: ${error.errorCode}`);
    }
    if (error.errorMessage) {
      logger.error(`Error Message: ${error.errorMessage}`);
    }
    if (error.stack) {
      logger.error(`Stack Trace:`, error.stack);
    }
    
    const additionalProps = Object.keys(error).filter(key => 
      !['errorCode', 'errorMessage', 'stack'].includes(key)
    );
    if (additionalProps.length > 0) {
      logger.error('Additional Error Properties:', 
        additionalProps.reduce((obj, key) => ({...obj, [key]: error[key]}), {})
      );
    }
  } else {
    // In production, log minimal information without exposing details
    logger.error(`Auth error occurred: ${error.errorCode || 'unknown'}`);
  }
};

export const authApi = {
  login: async (loginHint?: string) => {
    // Only log detailed config in development
    if (process.env.NODE_ENV !== 'production') {
      logger.debug("Login request scopes:", apiLoginRequest.scopes);
      logger.debug("MSAL Config:", {
        authority: msalConfig.auth.authority,
        clientId: "REDACTED",
        redirectUri: msalConfig.auth.redirectUri
      });
    }
    
    try {
      await msalInitPromise;
      logger.debug("MSAL initialization complete");
      
      const loginRedirectRequest = {
        ...apiLoginRequest,
        // Use select_account if no login hint, otherwise use none to avoid prompts
        prompt: loginHint ? 'none' : 'select_account',
        // If we have a login_hint, add it to skip the account selection screen
        ...(loginHint && { loginHint })
      };
      
      // Redirect to Microsoft login page
      await msalInstance.loginRedirect(loginRedirectRequest);
      // This function redirects, so the code below won't execute until after redirect back
      logger.debug("Redirect initiated successfully");
      return null;
    } catch (error) {
      logError("login", error);
      throw new Error(`Authentication failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },
  
  logout: async () => {
    try {
      await msalInitPromise;
      
      // Clear all browser storage related to MSAL
      const msalStorageKeys = Object.keys(sessionStorage)
        .filter(k => k.includes('msal') || k.includes('login') || k.includes('token') || k.includes('auth'));
      
      msalStorageKeys.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          logger.warn(`Failed to remove session storage key: ${key}`);
        }
      });
      
      const msalLocalStorageKeys = Object.keys(localStorage)
        .filter(k => k.includes('msal') || k.includes('login') || k.includes('token') || k.includes('auth'));
      
      msalLocalStorageKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          logger.warn(`Failed to remove local storage key: ${key}`);
        }
      });
      
      // Clear MSAL cache before logout
      msalInstance.clearCache();
      
      // Logout from Microsoft Entra with a full post-logout cleanup
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
        onRedirectNavigate: (url) => {
          // Log the redirect URL to help debug (but not in production)
          if (process.env.NODE_ENV !== 'production') {
            logger.debug("MSAL redirecting to:", url);
          }
          // Allow the redirect to happen
          return true;
        }
      });
    } catch (error) {
      logError("logout", error);
      
      // Even if logout fails, try to clear state and redirect to login
      logger.warn("Logout failed, clearing state manually");
      
      // Clear MSAL cache as a fallback
      try {
        msalInstance.clearCache();
      } catch (e) {
        logger.warn("Failed to clear MSAL cache");
      }
      
      // Redirect to login page
      window.location.href = '/login';
      
      throw new Error("Logout failed");
    }
  },
  
  // Method to get a fresh token (for API calls)
  getAccessToken: async () => {
    logger.debug("Getting access token");
    try {
      await msalInitPromise;
      
      const accounts = msalInstance.getAllAccounts();
      logger.debug(`Found ${accounts.length} account(s)`);
      
      if (accounts.length === 0) {
        logger.error("No authenticated accounts found");
        throw new Error("No authenticated accounts");
      }
      
      logger.debug("Attempting silent token acquisition");
      const silentRequest = {
        ...apiLoginRequest,
        account: accounts[0],
        // Explicitly include forceRefresh to false to use cached tokens when available
        forceRefresh: false
      };
      
      try {
        const response = await msalInstance.acquireTokenSilent(silentRequest);
        
        // Only log minimal token info even in development
        logger.debug("Token acquired successfully", {
          tokenType: response.tokenType,
          expiresOn: response.expiresOn ? new Date(response.expiresOn).toISOString() : 'unknown',
          scopes: response.scopes
        });
        
        return response.accessToken;
      } catch (silentError) {
        // If silent token acquisition fails due to interaction required
        if (silentError instanceof Error && 
            (silentError.message.includes("interaction_required") || 
             silentError.message.includes("consent_required"))) {
          
          logger.info("Silent token acquisition failed, attempting interactive acquisition");
          
          // Redirect to login page for interactive token acquisition
          // This will handle consent if needed
          const loginHint = accounts[0].username;
          await authApi.login(loginHint);
          return ""; // This will redirect, so return empty string
        }
        
        // For other errors, rethrow
        throw silentError;
      }
    } catch (error) {
      logError("getAccessToken", error);
      // Determine if we should redirect to login
      if ((error as any)?.errorCode === "user_login_error" || 
          (error as any)?.errorCode === "no_account_error") {
        logger.info("Redirecting to login after token acquisition failure");
        // Initialize if needed
        await msalInitPromise;
        await msalInstance.loginRedirect(apiLoginRequest);
      }
      throw new Error("Failed to acquire access token");
    }
  }
};